import React, {useState, useEffect} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    getAuth,
    signInWithEmailAndPassword,
    browserLocalPersistence, signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

import LoginLayout from './LoginLayout';
import { NEW_LINK_PARAM } from './loginPageParams';
import {Button, Modal, Form, Alert} from "react-bootstrap";
import {get, getDatabase, ref} from "firebase/database";
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // The set-password page sends the email along when its sign-in failed
    const [email, setEmail] = useState(location.state?.email || '');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    
    // Password reset modal states
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetError, setResetError] = useState(null);
    const [resetSuccess, setResetSuccess] = useState(false);
    const [sending, setSending] = useState(false);
    // True when opened from the "beställ en ny länk" link in the welcome email (?resetEmail=...)
    const [fromWelcomeEmail, setFromWelcomeEmail] = useState(false);

    const auth = getAuth();
    const from = location.state?.from?.pathname || '/';

    // Set document title
    useDocumentTitle('Molnfastigheter');

    // Older welcome emails link here with ?resetEmail=<address> when their password link
    // has expired: open the dialog prefilled so a new link is one click away. Sending
    // still requires the click — mail scanners prefetch links.
    // The set-password page links here with ?nyLank=1 when its link has expired: open
    // the dialog with an empty email, since the expired link no longer names one.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const requested = params.get('resetEmail');
        if (requested) {
            setEmail(requested);
            setResetEmail(requested);
            setFromWelcomeEmail(true);
        } else if (params.has(NEW_LINK_PARAM)) {
            setResetEmail('');
            setFromWelcomeEmail(false);
        } else {
            return;
        }
        setResetError(null);
        setResetSuccess(false);
        setShowResetModal(true);
    }, [location.search]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            // 1) Sign in
            await auth.setPersistence(browserLocalPersistence);
            const { user } = await signInWithEmailAndPassword(auth, email, password);

            // 2) Check their "accessToWeb" flag
            const db = getDatabase();
            const accessSnap = await get(ref(db, `users/userList/${user.uid}/accessToWeb`));
            if (accessSnap.exists() && accessSnap.val() === true) {
                // 3a) Allowed → route them in
                navigate(from, { replace: true });
            } else {
                // 3b) Denied → sign out immediately, show error
                await signOut(auth);
                setError('Ditt konto har inte behörighet att använda webbverktygen.');
            }
        } catch (err) {
            console.error(err);
            setError('Inloggningen misslyckades eller ett fel uppstod vid hämtning av data.');
        }
    };

    const handleForgotPassword = () => {
        // Pre-fill with current email if available
        setResetEmail(email);
        setResetError(null);
        setResetSuccess(false);
        setFromWelcomeEmail(false);
        setShowResetModal(true);
    };

    const handleCloseResetModal = () => {
        setShowResetModal(false);
        setResetEmail('');
        setResetError(null);
        setResetSuccess(false);
        setSending(false);
        setFromWelcomeEmail(false);
        if (location.search) {
            // Drop ?resetEmail / ?nyLank so a reload does not reopen the dialog
            navigate(location.pathname, { replace: true });
        }
    };

    const handleSendResetEmail = async () => {
        if (!resetEmail) {
            setResetError('Vänligen ange din e-postadress.');
            return;
        }

        setSending(true);
        setResetError(null);

        try {
            // Call the custom Firebase Function
            const sendPasswordResetEmailFunction = httpsCallable(functions, 'sendPasswordResetEmail');
            const result = await sendPasswordResetEmailFunction({ email: resetEmail });
            
            // The function always returns success (for security reasons)
            setResetSuccess(true);
        } catch (err) {
            console.error('Password reset error:', err);
            setResetError('Ett fel uppstod vid skickande av återställningsmail. Försök igen.');
        } finally {
            setSending(false);
        }
    };

    return (
        <LoginLayout>
                <form onSubmit={handleLogin} className="login-form">
                    {error && <div className="error-message">{error}</div>}

                    <span className={"field-title"}>
                        E-postadress
                    </span>
                    <input
                        type="email"
                        name="email"
                        autoComplete="username"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            setError(null);
                        }}
                        required
                        className="input-field"
                    />

                    <span className={"field-title"}>
                        Lösenord
                    </span>
                    <input
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setError(null);
                        }}
                        required
                        className="input-field"
                    />

                    <Button
                        variant={"outline-light"}
                        className="mx-auto block mt-5 text-sm"
                        style={{borderWidth: '2px', width: '120px'}}
                        type={"submit"}
                    >
                        Logga in
                    </Button>

                    <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="forgot-password-button"
                        style={{
                            marginTop: '20px'
                        }}
                    >
                        Glömt lösenord?
                    </button>
                </form>

            {/* Password Reset Modal */}
            <Modal show={showResetModal} onHide={handleCloseResetModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{fromWelcomeEmail ? 'Ny länk för att välja lösenord' : 'Återställ lösenord'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!resetSuccess ? (
                        <>
                            {fromWelcomeEmail ? (
                                <p>Länken i välkomstmailet gäller bara en kort tid. Klicka på knappen så skickar vi en ny länk till adressen nedan.</p>
                            ) : (
                                <p>Ange din e-postadress så skickar vi instruktioner för att återställa ditt lösenord.</p>
                            )}
                            
                            <Form.Group className="mb-3">
                                <Form.Label>E-postadress</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    placeholder="Ange din e-postadress"
                                    disabled={sending}
                                />
                            </Form.Group>

                            {resetError && (
                                <Alert variant="danger" className="mb-3">
                                    {resetError}
                                </Alert>
                            )}
                        </>
                    ) : (
                        <div className="text-center">
                            <Alert variant="success" className="mb-3">
                                <strong>
                                    {fromWelcomeEmail
                                        ? 'En ny länk för att välja lösenord har skickats till din e-post.'
                                        : 'Ett mail med instruktioner på hur du återställer ditt lösenord har skickats.'}
                                </strong>
                                <br />
                                <small>Om du inte får mailet, titta i skräpfiltret.</small>
                            </Alert>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {!resetSuccess ? (
                        <>
                            <Button variant="secondary" onClick={handleCloseResetModal} disabled={sending}>
                                Avbryt
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleSendResetEmail}
                                disabled={sending || !resetEmail}
                            >
                                {sending ? 'Skickar...' : (fromWelcomeEmail ? 'Skicka ny länk' : 'Skicka återställningsmail')}
                            </Button>
                        </>
                    ) : (
                        <Button variant="primary" onClick={handleCloseResetModal}>
                            Stäng
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

        </LoginLayout>
    );
};

export default LoginPage;
